// JobsDrawerFormFields.tsx
import { Add, List } from '@mui/icons-material';
import { AutocompleteInputChangeReason, Grid2, SelectChangeEvent, Typography } from '@mui/material';
import { JobSiteAutocompleteOption, useListJobSitesAutocomplete } from '@/jobs/application/adapters/listJobSitesAutocomplete';
import { isValid, parse } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import { ALLOWED_CALLER_ID_TYPES_SET } from '@/customers/application/types/CustomerTypes';
import { AutocompleteOption } from '@/common/presentation/hooks/useAutocomplete';
import CustomerModal from '@/customers/presentation/pages/CustomersPage/CustomersManagementTab/CustomerModal';
import FormikAutocomplete from '@/common/presentation/components/forms/fields/FormikAutocomplete';
import { FormikDatePicker } from '@/common/presentation/components/forms/fields/FormikDatePicker';
import { FormikSelect } from '@/common/presentation/components/forms/fields/FormikSelect';
import { FormikTextField } from '@/common/presentation/components/forms/fields/FormikTextField';
import { FormikTimePicker } from '@/common/presentation/components/forms/fields/FormikTimePicker';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { JobFormData } from '@/jobs/application/types/JobTypes';
import { JobSiteCreateModal } from './JobSiteCreateModal';
import { JobTimezoneField } from '@/jobs/presentation/components/JobTimezoneField';
import { LoggerService } from '@/common/infrastructure/services/LoggerService';
import tid from '@/common/infrastructure/config/tid';
import { useContactOptions } from '@/common/presentation/hooks/useContactOptions';
import { useFocus } from '@/common/presentation/hooks/useFocus';
import { useFormikContext } from 'formik';
import { useGetCustomerCallerId } from '@/customers/application/adapters/getCustomerCallerId';
import { useJobFormData } from '@/jobs/presentation/hooks/useJobFormData';
import { useJobSiteTimeZone } from '@/jobs/presentation/hooks/useJobSiteTimeZone';
import { useJobTimezone } from '@/jobs/presentation/hooks/useJobTimezone';
import { useListCustomersAutocomplete } from '@/jobs/application/adapters/listCustomersAutocomplete';
import { useListEquipmentSizesAutocomplete } from '@/jobs/application/adapters/listEquipmentSizesAutocomplete';
import { TEXT_COLORS } from '@/common/presentation/constants/colors';
import { useListJobTypesAutocomplete } from '@/jobs/application/adapters/listJobTypesAutocomplete';
import { useGetJobFormOptions } from '@/jobs/application/adapters/getJobFormOptions';
import { useVendorAutocomplete } from '@/jobs/presentation/hooks/useVendorAutocomplete';
import { useNavigate } from 'react-router';
import { TimeZoneService } from '@/jobs/application/services/TimeZoneService';
import { useTimeZoneTypeaheadFetch } from '@/jobs/application/adapters/listTimeZonesAutocomplete';
import { useCallback } from 'react';

interface JobsDrawerFormFieldsProps {
  isNew: boolean;
  jobId: number;
  isOpen: boolean;
  isCreateCustomerModalOpen: boolean;
  setIsCreateCustomerModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  createJobSiteModal: boolean;
  setCreateJobSiteModal: React.Dispatch<React.SetStateAction<boolean>>;
}

// Define a type for autocomplete options with contact information
export interface AutocompleteOptionWithContact {
  value: string | number;
  label: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
}

export default function JobsDrawerFormFields({
  isNew,
  jobId,
  isOpen,
  createJobSiteModal,
  setCreateJobSiteModal,
  isCreateCustomerModalOpen,
  setIsCreateCustomerModalOpen
}: Readonly<JobsDrawerFormFieldsProps>) {
  const { values, setFieldValue, setFieldTouched, validateField } = useFormikContext<JobFormData>();
  const [currentCustomerOption, setCurrentCustomerOption] = useState<
    { value: string | number; label: string; email?: string | null; phone?: string | null } | undefined
  >(undefined);
  const [selectedJobSite, setSelectedJobSite] = useState<JobSiteAutocompleteOption | null>(null);
  const [preventUserTimezoneFallback, setPreventUserTimezoneFallback] = useState(false);

  /**
   * Cache the selected customer data to avoid unnecessary API calls.
   * When a customer is selected, we store their full data including contacts
   * to prevent redundant getCustomerCallerId queries.
   */
  type CustomerData = {
    id?: number | null;
    companyName?: string | null;
    primaryEmail?: string | null;
    primaryPhone?: string | null;
    contacts?: Array<{
      id?: number | null;
      firstName?: string | null;
      lastName?: string | null;
      contactInformation?: Array<{
        id?: number | null;
        type?: string | null;
        contact?: string | null;
      }> | null;
    }> | null;
  };
  const [cachedCustomerData, setCachedCustomerData] = useState<CustomerData | null>(null);

  const navigate = useNavigate();
  const { rawJob, jobLoading } = useJobFormData(jobId);
  const { ref: customerFieldRef } = useFocus({ trigger: isOpen });

  // Create a logger instance for this component
  const logger = useMemo(() => LoggerService.create('JobsDrawerFormFields'), []);

  // Timezone hooks
  const {
    userTimezone,
    initialTimeZone,
    timezoneError,
    timezoneLoading,
    clearTimezoneError,
    fetchTimeZonesWithInitial,
    handleTimezoneSelection
  } = useJobTimezone({ rawJob, preventUserTimezoneFallback });

  const { initialTimeZones } = useTimeZoneTypeaheadFetch();
  const timeZoneService = useMemo(() => TimeZoneService.getInstance(), []);

  // Job site timezone integration
  const {
    isPopulatingTimeZone,
    timeZoneError: jobSiteTimeZoneError,
    clearTimeZoneError: clearJobSiteTimeZoneError
  } = useJobSiteTimeZone({
    selectedJobSite: selectedJobSite
      ? {
          id: selectedJobSite.value,
          jobSiteName: selectedJobSite.label,
          address: selectedJobSite.address ?? undefined,
          latitude: selectedJobSite.latitude ?? undefined,
          longitude: selectedJobSite.longitude ?? undefined
        }
      : null,
    availableTimezones: initialTimeZones,
    isNewJob: isNew,
    onTimeZonePopulated: (timeZone) => {
      // Update the timezone field when populated from job site
      logger.debug('Timezone populated from job site:', timeZone);
      setFieldValue('timeZone', timeZone.value);
    },
    onError: (error) => {
      // Handle timezone population errors
      logger.warn('Timezone population error:', error);
    },
    onJobSiteSelected: () => {
      // Prevent user timezone fallback when jobsite is selected
      setPreventUserTimezoneFallback(true);
    }
  });

  // Function to populate timezone after jobsite creation
  const handleJobSiteCreated = useCallback(
    async (jobSiteData: {
      id: number | null;
      jobSiteName: string | null;
      address: string | null;
      latitude?: number | null;
      longitude?: number | null;
    }) => {
      if (!jobSiteData.latitude || !jobSiteData.longitude) {
        logger.warn('JobSite created without coordinates, cannot populate timezone', jobSiteData);
        return;
      }

      try {
        logger.debug('Populating timezone for newly created jobsite', jobSiteData);
        const timeZoneOption = await timeZoneService.getTimezoneOptionForJobSite(
          jobSiteData.latitude,
          jobSiteData.longitude,
          initialTimeZones,
          jobSiteData.address || undefined
        );

        if (timeZoneOption) {
          setFieldValue('timeZone', timeZoneOption.value);
          logger.info('Timezone populated from newly created jobsite', {
            timeZoneId: timeZoneOption.value,
            timeZoneName: timeZoneOption.label
          });
        }
      } catch (error) {
        logger.warn('Failed to populate timezone from newly created jobsite', error);
      }
    },
    [timeZoneService, initialTimeZones, setFieldValue, logger]
  );

  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [jobSiteSearchTerm, setJobSiteSearchTerm] = useState('');
  const [jobTypeSearchTerm, setJobTypeSearchTerm] = useState('');
  const [equipmentSizeSearchTerm, setEquipmentSizeSearchTerm] = useState('');

  const {
    options: customerOptions,
    loading: customerLoading,
    filterOptions: customerFilterOptions,
    rawCustomerData
  } = useListCustomersAutocomplete({ searchTerm: customerSearchTerm });

  const {
    options: jobSiteOptions,
    loading: jobSiteLoading,
    filterOptions: jobSiteFilterOptions,
    refetch: refetchJobSites
  } = useListJobSitesAutocomplete(jobSiteSearchTerm, 1000, values.customerId ?? undefined);

  const {
    options: jobTypeOptions,
    loading: jobTypeLoading,
    filterOptions: jobTypeFilterOptions
  } = useListJobTypesAutocomplete({ searchTerm: jobTypeSearchTerm });

  const {
    options: equipmentSizeOptions,
    loading: equipmentSizeLoading,
    filterOptions: equipmentSizeFilterOptions
  } = useListEquipmentSizesAutocomplete({ searchTerm: equipmentSizeSearchTerm });

  // Get available vendors from form options
  const { availableVendors, loading: vendorsLoading } = useGetJobFormOptions(false);

  // Use vendor autocomplete hook
  const {
    vendorOptions,
    vendorFilterOptions,
    handleVendorInputChange,
    initialVendorOption,
    loading: vendorAutocompleteLoading
  } = useVendorAutocomplete({
    availableVendors,
    loading: vendorsLoading,
    rawVendor: rawJob?.vendor,
    selectedVendorId: values.vendorId
  });

  // Find the selected customer data from the raw customer data or use cached data
  // React Compiler automatically optimizes this derivation
  let selectedCustomerData: CustomerData | undefined = undefined;

  if (values.customerId) {
    // Try to find in current search results
    const fromSearch = rawCustomerData.find((customer) => customer.id === values.customerId);
    if (fromSearch) {
      selectedCustomerData = fromSearch;
    } else if (cachedCustomerData && cachedCustomerData.id === values.customerId) {
      // Fall back to cached data if available
      selectedCustomerData = cachedCustomerData;
    }
  }

  // Update cache when customer data is found in search results
  useEffect(() => {
    if (selectedCustomerData && selectedCustomerData.id === values.customerId) {
      setCachedCustomerData(selectedCustomerData);
    }
  }, [selectedCustomerData?.id, values.customerId]);

  // Get customer caller ID information when a customer is selected
  const {
    customerDetails: customerCallerDetails,
    loading: customerCallerLoading,
    contacts: customerContacts
  } = useGetCustomerCallerId(values.customerId ?? undefined, selectedCustomerData);

  const { options: contactOptions, initialCallerId } = useContactOptions({
    contacts: customerContacts,
    rawCaller: rawJob?.caller,
    allowedTypes: ALLOWED_CALLER_ID_TYPES_SET
  });

  // Initialize currentCustomerOption with existing job data
  const initialCustomerOption = useMemo(() => {
    if (!rawJob?.customer) return undefined;
    return {
      value: rawJob.customer.id ?? 0,
      label: rawJob.customer.companyName ?? 'Unknown Customer',
      email: rawJob.customer.primaryEmail,
      phone: rawJob.customer.primaryPhone
    };
  }, [rawJob?.customer?.id, rawJob?.customer?.companyName, rawJob?.customer?.primaryEmail, rawJob?.customer?.primaryPhone]);

  // Set initial customer option when rawJob changes (only on customer ID change)
  useEffect(() => {
    if (initialCustomerOption && initialCustomerOption.value !== currentCustomerOption?.value) {
      setCurrentCustomerOption(initialCustomerOption);
    }
  }, [initialCustomerOption?.value]); // Only re-run when customer ID changes

  // Set initial job site when rawJob changes
  // Use only jobSite.id to avoid unnecessary re-renders when other jobSite properties change
  useEffect(() => {
    if (rawJob?.jobSite) {
      const jobSiteOption: JobSiteAutocompleteOption = {
        value: rawJob.jobSite.id ?? 0,
        label: rawJob.jobSite.jobSiteName ?? 'Unknown JobSite',
        address: rawJob.jobSite.address,
        latitude: rawJob.jobSite.latitude,
        longitude: rawJob.jobSite.longitude
      };
      setSelectedJobSite(jobSiteOption);
    } else {
      setSelectedJobSite(null);
    }
  }, [rawJob?.jobSite?.id]); // Use only ID to prevent unnecessary re-renders

  // Handle customer caller ID data when it's loaded (only log once per customer)
  useEffect(() => {
    if (customerCallerDetails?.id && values.customerId) {
      // Only log in development to reduce console noise
      if (process.env.NODE_ENV === 'development') {
        logger.debug('Customer caller ID data loaded:', {
          customerId: values.customerId,
          companyName: customerCallerDetails.companyName || 'N/A',
          contactsCount: customerContacts?.length || 0
        });
      }
    }
  }, [customerCallerDetails?.id, values.customerId, customerCallerDetails?.companyName, customerContacts?.length, logger]);

  const handleCustomerInputChange = (_event: React.SyntheticEvent, value: string) => {
    setCustomerSearchTerm(value);
  };

  const handleJobSiteInputChange = (_event: React.SyntheticEvent, value: string, reason?: AutocompleteInputChangeReason) => {
    if (reason !== 'selectOption' && reason !== 'reset') {
      setJobSiteSearchTerm(value);
    }
  };

  const handleJobTypeInputChange = (_event: React.SyntheticEvent, value: string) => {
    setJobTypeSearchTerm(value);
  };

  const handleEquipmentSizeInputChange = (_event: React.SyntheticEvent, value: string) => {
    setEquipmentSizeSearchTerm(value);
  };

  const handleContactChange = (event: SelectChangeEvent<unknown>) => {
    setFieldValue('callerId', event.target.value);
  };

  const handleCreateCustomerSuccess = async (customer: { id: number; companyName: string }) => {
    // Create a new option with the customer data
    const newCustomerOption = {
      value: customer.id,
      label: customer.companyName,
      email: null,
      phone: null
    };

    // Set the current customer option to display in the autocomplete
    setCurrentCustomerOption(newCustomerOption);

    // Set the search term to the new customer's name to trigger a search
    setCustomerSearchTerm(customer.companyName);

    // Set the form value to the new customer's ID
    setFieldValue('customerId', customer.id);
    // Mark field as touched and validate to clear any required errors
    setFieldTouched('customerId', true);
    validateField('customerId');
  };

  const handleTimeFormat = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.target?.id || !e.target?.value) return;
    const inputId = e.target.id;
    let rawInput = e.target.value; // user typed value
    if (rawInput && !/am|pm/i.test(rawInput)) {
      rawInput = rawInput.replace(' aa', '');
      const withAm = `${rawInput} AM`;
      const parsed = parse(withAm, 'hh:mm a', new Date());
      if (isValid(parsed)) {
        setFieldValue(inputId, parsed);
      }
    }
  };

  const customerActions = [
    { label: 'View All Customers', icon: <List />, onClick: () => navigate('/customers') },
    { label: 'Add New Customer', icon: <Add />, onClick: () => setIsCreateCustomerModalOpen(true) }
  ];

  const jobSiteActions = [
    { label: 'View All JobSites', icon: <List />, onClick: () => navigate('/jobs?module=jobs&submodule=jobSites') },
    { label: 'Add New JobSite', icon: <Add />, onClick: () => setCreateJobSiteModal(true) }
  ];

  return (
    <>
      <Grid2 container spacing={1} sx={{ pt: 3 }} {...tid('jobs:dispatch:form-fields')}>
        {/* Job ID */}
        {!isNew && (
          <Grid2 size={12}>
            <FormikTextField<JobFormData> id="id" label="ID" disabled fullWidth testId="jobs:dispatch:id-input" />
          </Grid2>
        )}
        <Typography sx={{ marginLeft: '0.5em' }} variant="subtitle1">
          Customer information
        </Typography>
        {/* Customer*/}
        <Grid2 size={12}>
          <FormikAutocomplete<{ value: string | number; label: string; email?: string | null; phone?: string | null }, JobFormData>
            id="customerId"
            label="Customer"
            {...tid('jobs:dispatch:customer-autocomplete')}
            value={currentCustomerOption}
            options={customerOptions}
            optionsloading={customerLoading || jobLoading}
            filterOptions={customerFilterOptions}
            onInputChange={handleCustomerInputChange}
            getOptionLabel={(option) => option?.label ?? 'Unknown Customer'}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            actions={customerActions}
            inputRef={customerFieldRef}
            autoFocus
            onChange={(value) => {
              if (Array.isArray(value)) {
                setCurrentCustomerOption(value[0] || undefined);
              } else {
                setCurrentCustomerOption(value || undefined);
              }
              setFieldValue('jobSiteId', '');
              setFieldValue('callerId', ''); // Clear contact selection when customer changes
            }}
            required
          />
        </Grid2>
        {/* Contact Selection */}
        <Grid2 size={12}>
          <FormikSelect<JobFormData>
            id="callerId"
            label="Caller ID"
            options={contactOptions}
            placeholder={customerCallerLoading ? 'Loading contacts...' : 'Select a contact'}
            disabled={customerCallerLoading || contactOptions.length === 0}
            value={values.callerId ?? initialCallerId ?? ''}
            onChange={handleContactChange}
            {...tid('jobs:dispatch:contact-select')}
          />
        </Grid2>
        <Typography sx={{ marginLeft: '0.5em' }} variant="subtitle1">
          Job information
        </Typography>
        {/* Job site */}
        <Grid2 size={12}>
          <FormikAutocomplete<AutocompleteOptionWithContact, JobFormData>
            id="jobSiteId"
            label="Jobsite"
            initialOption={
              rawJob?.jobSite ? { value: rawJob.jobSite.id ?? 0, label: rawJob.jobSite.jobSiteName ?? 'Unknown JobSite' } : undefined
            }
            {...tid('jobs:dispatch:jobsite-autocomplete')}
            options={jobSiteOptions}
            optionsloading={jobSiteLoading || jobLoading}
            filterOptions={jobSiteFilterOptions}
            onInputChange={handleJobSiteInputChange}
            getOptionLabel={(option) => {
              const address = option.address ? ` - ${option.address}` : '';
              return option.label ? `${option.label}${address}` : 'Unknown JobSite';
            }}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            actions={jobSiteActions}
            disabled={!Boolean(values.customerId)}
            onChange={(value) => {
              // Update selected job site for timezone population
              // FormikAutocomplete's internal handleChange already calls setFieldValue('jobSiteId', value)
              // So we only need to update selectedJobSite state here
              const newJobSite = Array.isArray(value) ? (value[0] as JobSiteAutocompleteOption) : (value as JobSiteAutocompleteOption);

              if (newJobSite) {
                // Set the selected jobsite state with full location data
                // This will trigger useJobSiteTimeZone hook to populate timezone
                setSelectedJobSite(newJobSite);
              } else {
                // Clear state when jobsite is cleared
                setSelectedJobSite(null);
              }
            }}
            required
          />
        </Grid2>
        {/* Job Name */}
        <Grid2 size={12}>
          <FormikTextField<JobFormData> id="jobName" label="Job Name" testId="jobs:dispatch:name-input" />
        </Grid2>
        <Grid2 size={12}>
          <FormikTextField<JobFormData>
            id="poNumber"
            label="PO number"
            {...tid('jobs:dispatch:po-number-input')}
            placeholder="Enter PO Number"
          />
        </Grid2>
        {/* Vendor */}
        <Grid2 size={12}>
          <FormikAutocomplete<AutocompleteOption, JobFormData>
            id="vendorId"
            label="Vendor"
            initialOption={initialVendorOption}
            options={vendorOptions}
            optionsloading={vendorAutocompleteLoading || jobLoading}
            filterOptions={vendorFilterOptions}
            onInputChange={handleVendorInputChange}
            getOptionLabel={(option) => option?.label ?? 'Unknown Vendor'}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            {...tid('jobs:dispatch:vendor-select')}
          />
        </Grid2>
        {/* Job Type */}
        <Grid2 size={12}>
          <FormikAutocomplete<AutocompleteOption, JobFormData>
            id="typeId"
            label="Job Type"
            initialOption={rawJob?.type?.id != null ? { label: rawJob.type.name ?? '', value: rawJob.type.id } : undefined}
            options={jobTypeOptions}
            optionsloading={jobTypeLoading || jobLoading}
            filterOptions={jobTypeFilterOptions}
            onInputChange={handleJobTypeInputChange}
            getOptionLabel={(option) => option?.label ?? 'Unknown Job Type'}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            {...tid('jobs:dispatch:jobtype-select')}
            required
          />
        </Grid2>
        {/* Jobsite Size */}
        <Grid2 size={12}>
          <FormikAutocomplete<AutocompleteOption, JobFormData>
            id="sizeRequestedId"
            label="Size Requested"
            initialOption={
              rawJob?.sizeRequested?.id != null ? { label: rawJob.sizeRequested.name ?? '', value: rawJob.sizeRequested.id } : undefined
            }
            options={equipmentSizeOptions}
            optionsloading={equipmentSizeLoading || jobLoading}
            filterOptions={equipmentSizeFilterOptions}
            onInputChange={handleEquipmentSizeInputChange}
            getOptionLabel={(option) => option?.label ?? 'Unknown Size'}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            {...tid('jobs:dispatch:size-requested-select')}
            required
          />
        </Grid2>
        <Typography sx={{ marginLeft: '0.5em' }} variant="subtitle1">
          Date
        </Typography>
        {/* Select Date */}
        <Grid2 size={12}>
          <FormikDatePicker id="startDate" label="Job Start Date" testId="jobs:dispatch:start-date-input" required />
        </Grid2>
        {/* Estimated Hours */}
        <Grid2 size={12}>
          <FormikTextField<JobFormData>
            id="estimatedHours"
            label="Est. Hours"
            type="number"
            {...tid('jobs:dispatch:estimated-hours-input')}
          />
        </Grid2>
        <Typography sx={{ marginLeft: '0.5em', mt: 2 }} variant="subtitle1">
          Daily Dispatch Times
        </Typography>
        {/* Job Start Time */}
        <Grid2 size={12}>
          <FormikTimePicker
            id="startTime"
            label="Job Start Time"
            {...tid('jobs:dispatch:start-time-input')}
            views={['hours', 'minutes']}
            fullWidth={true}
            onBlur={handleTimeFormat}
          />
        </Grid2>
        {/* Job Time Zone */}
        <Grid2 size={12} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
          <Grid2 size={{ xs: 12, sm: 'auto' }} sx={{ minWidth: { sm: '312px' }, maxWidth: { sm: '312px' } }}>
            <JobTimezoneField
              userTimezone={userTimezone}
              initialTimeZone={initialTimeZone}
              timezoneError={timezoneError || (jobSiteTimeZoneError ? { message: jobSiteTimeZoneError } : null)}
              timezoneLoading={timezoneLoading || isPopulatingTimeZone}
              clearTimezoneError={() => {
                clearTimezoneError();
                clearJobSiteTimeZoneError();
              }}
              fetchTimeZonesWithInitial={fetchTimeZonesWithInitial}
              handleTimezoneSelection={handleTimezoneSelection}
              showHelperText={false}
              showError={true}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 'grow' }} sx={{ flex: { sm: '1 0 0' } }}>
            <Typography variant="caption" sx={{ lineHeight: 1.66, color: TEXT_COLORS.TEXT_SECONDARY }}>
              Use the time zone where the job will take place, regardless of the client or dispatch location.
            </Typography>
          </Grid2>
        </Grid2>
        {/* Notes for Crew */}
        <Grid2 size={12}>
          <FormikTextField<JobFormData>
            id="notesForCrew"
            label="Notes for Crew"
            multiline
            rows={4}
            iconEndAdornment={<InfoOutlined {...tid('jobs:dispatch:notes-for-crew-info-icon')} />}
            tooltipText="Use Notes for Crew for important instructions that crew members must acknowledge before starting work. These notes appear on printed job tickets."
            tooltipPlacement="top"
            placeholder="Enter Notes for Crew"
            {...tid('jobs:dispatch:notes-for-crew-input')}
          />
        </Grid2>
      </Grid2>

      <CustomerModal
        open={isCreateCustomerModalOpen}
        onClose={() => setIsCreateCustomerModalOpen(false)}
        onSuccess={handleCreateCustomerSuccess}
      />

      <JobSiteCreateModal
        customerId={values.customerId ?? 0}
        customerName={currentCustomerOption?.label ?? ''}
        isOpen={createJobSiteModal && !!values.customerId}
        toggleModal={() => setCreateJobSiteModal((state) => !state)}
        setJobSiteFieldValue={(jobSiteId) => setFieldValue('jobSiteId', jobSiteId)}
        refetchJobSites={refetchJobSites}
        onJobSiteCreated={handleJobSiteCreated}
      />
    </>
  );
}
